import React from 'react';
import { connect } from 'react-redux';
import track from 'react-tracking';
import { string, shape, func } from 'prop-types';
import ReactMarkdown from 'markdown-to-jsx';
import Overlay, { OverlayAnimation } from '@ynap/overlay';
import Spinner from '@ynap/spinner';
import BEMHelper from '@ynap/bem-helper';
import { Button } from '@ynap/button';
import { authActions } from '@ynap/auth-state';
import { getHeadConfig, getLocale } from '@ynap/config-state';
import Notification from '@ynap/notification';
import { trackReadPrivacyPolicy, trackReadTermsAndConditions, trackAgreeTerms, trackDisagreeTerms } from '../analytics';

const classNames = new BEMHelper('LoginConsent');

@track()
class LoginConsent extends React.Component {
    static propTypes = {
        loginForm: string,
        name: string,
        locale: shape(),
        config: shape({
            appName: string,
            links: shape({
                termsAndConditions: string,
                privacyPolicy: string,
            }),
        }),
        messages: shape({
            loginConsent: shape({
                teInstruction: func,
                teNotification: func,
                title: func,
                overlayBody1: func,
                overlayBody2: func,
                termsAndConditions: func,
                privacyPolicy: func,
                pleaseNote: func,
                submit: func,
                and: func,
            }),
        }),
    };

    @track(trackAgreeTerms)
    handleClick = () => {
        const { input } = this.props;
        input.onChange(true);
    };

    @track(trackDisagreeTerms)
    handleCloseClick = () => {
        const { input } = this.props;
        input.onChange(null);
        event.preventDefault();
        return false;
    };

    constructor(props) {
        super(props);
        this.handleClickTermsConditions = this.handleClickTermsConditions.bind(this);
        this.handleClickPrivacy = this.handleClickPrivacy.bind(this);
    }

    @track(trackReadTermsAndConditions)
    handleClickTermsConditions() { }

    @track(trackReadPrivacyPolicy)
    handleClickPrivacy() { }

    render() {
        const { messages, className, input, config, locale } = this.props;
        const { links } = config;
        const loading = false;
        const consent = messages.loginConsent;

        const termsAndConditionsLink = localize(locale, links.termsAndConditions);
        const privacyPolicyLink = localize(locale, links.privacyPolicy);
        const dontProceedLink = localize(locale, links.consentDontProceed);

        const tierExpansionContent = consent.teInstruction && consent.teNotification && true;

        return (
            <div className={classNames(null, null, className)}>
                <OverlayAnimation>
                    {input.value === false ? (
                        <Overlay key="overlay" className={classNames('consentOverlay')} onClose={this.handleCloseClick} type="alert">
                            <Spinner loading={loading}>
                                <div className={classNames('wrap')}>
                                    <div className={classNames('title')}>{consent.title({ tierExpansionContent })}</div>
                                </div>
                                <div className={classNames('divider')} />
                                <div className={classNames('wrap')}>
                                    <div className={classNames('instruction')}>
                                        {tierExpansionContent
                                            ? <p>{
                                                <ReactMarkdown
                                                    options={{
                                                        overrides: {
                                                            a: {
                                                                component: Button,
                                                                props: {
                                                                    className: classNames('link'),
                                                                    target: '_blank',
                                                                    rel: 'noopener noreferrer',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {consent.teInstruction({ locale: locale.id })}
                                                </ReactMarkdown>
                                            }</p>
                                            : <>
                                                <p>{consent.overlayBody1()}</p>
                                                <p>
                                                    <a
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={classNames('link')}
                                                        href={termsAndConditionsLink}
                                                        onClick={this.handleClickTermsConditions}
                                                    >
                                                        {consent.termsAndConditions()}
                                                    </a>
                                                    <div className={classNames('and')}>{consent.and()}</div>
                                                    <a
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={classNames('link')}
                                                        href={privacyPolicyLink}
                                                        onClick={this.handleClickPrivacy}
                                                    >
                                                        {consent.privacyPolicy()}
                                                    </a>
                                                </p>
                                                <p>{consent.overlayBody2()}</p>
                                            </>
                                        }

                                    </div>
                                    <div className={classNames('ctas', tierExpansionContent && 'tierExpansion')}>
                                        <Button type="button" onClick={this.handleClick} className={classNames('submit')} disabled={loading}>
                                            {consent.submit()}
                                        </Button>
                                        <Notification>
                                            <p className={classNames('please-note', tierExpansionContent && 'tierExpansion')}>
                                                {tierExpansionContent
                                                    ? <p>{
                                                        <ReactMarkdown
                                                            options={{
                                                                overrides: {
                                                                    a: {
                                                                        props: {
                                                                            className: classNames('link', tierExpansionContent && 'tierExpansion'),
                                                                            rel: 'noopener noreferrer',
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            {consent.teNotification({ locale: locale.id })}
                                                        </ReactMarkdown>
                                                    }</p>
                                                    : <>
                                                        {consent.pleaseNote1()}
                                                        <a className={classNames('link', 'dont-proceed')} href={dontProceedLink}>
                                                            {consent.dontProceed()}
                                                        </a>
                                                        {consent.pleaseNote2()}
                                                    </>
                                                }
                                            </p>
                                        </Notification>
                                    </div>
                                </div>
                            </Spinner>
                        </Overlay>
                    ) : null}
                </OverlayAnimation>
            </div>
        );
    }
}

export default connect(
    (state) => ({
        config: state.config.head ? getHeadConfig(state) : state.config,
        locale: getLocale(state),
    }),
    authActions,
)(LoginConsent);

export const localize = (locale, text) => {
    if (text && text.length) {
        return text.replace('{language}', locale.language).replace('{country}', locale.country).toLowerCase();
    }
    return text;
};
